/**
 * useCartActions — single hook that wraps the server cart API with optimistic
 * UI for the ADD / increment / decrement flow used by every product card.
 *
 * Maintains in-memory `quantities` + `cartItemIds` maps keyed by `product.id`
 * AND `store.id`, so the same component can be reused across stores in the
 * same page (e.g. global "Deals" rail that spans stores).
 *
 * Usage:
 *   const { quantities, add, increment, decrement, primeFromStore } = useCartActions();
 *   primeFromStore(storeId);   // optional one-shot: sync state with server cart
 *   <ProductCard
 *      product={p}
 *      qty={quantities.get(`${p.store_id}:${p.id}`) ?? 0}
 *      onAdd={() => add(p)}
 *      onIncrement={() => increment(p)}
 *      onDecrement={() => decrement(p)}
 *   />
 */
import { useCallback, useState } from 'react';
import { cartAPI } from '../api/cart';
import { Product } from '../api/products';
import { useAuthStore } from '../stores/authStore';
import { notify } from '../design';

type Key = string;
const keyOf = (p: Pick<Product, 'id' | 'store_id'>) => `${p.store_id}:${p.id}`;

export function useCartActions() {
  const isAuthed = useAuthStore((s) => s.isAuthenticated);
  const [quantities, setQuantities] = useState<Map<Key, number>>(new Map());
  const [cartItemIds, setCartItemIds] = useState<Map<Key, number>>(new Map());
  const [busy, setBusy] = useState<Map<Key, boolean>>(new Map());

  const setBusyFor = (k: Key, v: boolean) =>
    setBusy((prev) => {
      const m = new Map(prev);
      if (v) m.set(k, true);
      else m.delete(k);
      return m;
    });

  /** Pull the existing server cart for `storeId` so qty steppers start
   *  pre-filled. Failures are silent — the user just sees ADD. */
  const primeFromStore = useCallback(async (storeId: number) => {
    if (!isAuthed) return;
    try {
      const cart = await cartAPI.getCart(storeId);
      setQuantities((prev) => {
        const m = new Map(prev);
        for (const it of cart.items || []) m.set(`${storeId}:${it.product_id}`, it.quantity);
        return m;
      });
      setCartItemIds((prev) => {
        const m = new Map(prev);
        for (const it of cart.items || []) m.set(`${storeId}:${it.product_id}`, it.id);
        return m;
      });
    } catch {
      /* ignore */
    }
  }, [isAuthed]);

  const add = useCallback(async (p: Product) => {
    if (!isAuthed) {
      notify.error('Please sign in to add to cart');
      return;
    }
    const k = keyOf(p);
    setBusyFor(k, true);
    setQuantities((prev) => {
      const m = new Map(prev);
      m.set(k, (m.get(k) ?? 0) + 1);
      return m;
    });
    try {
      const item = await cartAPI.addToCart(p.store_id, p.id, 1);
      setCartItemIds((prev) => {
        const m = new Map(prev);
        m.set(k, item.id);
        return m;
      });
      notify.success(`Added ${p.name}`, { duration: 1500 });
    } catch (err: any) {
      // Rollback
      setQuantities((prev) => {
        const m = new Map(prev);
        const cur = m.get(k) ?? 0;
        if (cur <= 1) m.delete(k);
        else m.set(k, cur - 1);
        return m;
      });
      notify.error(err?.response?.data?.detail || 'Could not add to cart');
    } finally {
      setBusyFor(k, false);
    }
  }, [isAuthed]);

  const increment = useCallback(async (p: Product) => {
    const k = keyOf(p);
    const cur = quantities.get(k) ?? 0;
    const cartItemId = cartItemIds.get(k);
    setQuantities((prev) => {
      const m = new Map(prev);
      m.set(k, cur + 1);
      return m;
    });
    try {
      if (cartItemId) await cartAPI.updateCartItem(cartItemId, cur + 1);
      else {
        const item = await cartAPI.addToCart(p.store_id, p.id, cur + 1);
        setCartItemIds((prev) => { const m = new Map(prev); m.set(k, item.id); return m; });
      }
    } catch (err: any) {
      setQuantities((prev) => { const m = new Map(prev); m.set(k, cur); return m; });
      notify.error(err?.response?.data?.detail || 'Could not update');
    }
  }, [quantities, cartItemIds]);

  const decrement = useCallback(async (p: Product) => {
    const k = keyOf(p);
    const cur = quantities.get(k) ?? 0;
    const cartItemId = cartItemIds.get(k);
    const next = cur - 1;
    setQuantities((prev) => {
      const m = new Map(prev);
      if (next <= 0) m.delete(k);
      else m.set(k, next);
      return m;
    });
    try {
      if (next <= 0) {
        if (cartItemId) await cartAPI.removeFromCart(cartItemId);
        setCartItemIds((prev) => { const m = new Map(prev); m.delete(k); return m; });
      } else if (cartItemId) {
        await cartAPI.updateCartItem(cartItemId, next);
      }
    } catch (err: any) {
      setQuantities((prev) => { const m = new Map(prev); m.set(k, cur); return m; });
      notify.error(err?.response?.data?.detail || 'Could not update');
    }
  }, [quantities, cartItemIds]);

  return {
    quantities,
    cartItemIds,
    busy,
    keyOf,
    add,
    increment,
    decrement,
    primeFromStore,
    /** Convenience getter for components that don't want to import `keyOf`. */
    getQty: (p: Pick<Product, 'id' | 'store_id'>) => quantities.get(keyOf(p)) ?? 0,
    isBusy: (p: Pick<Product, 'id' | 'store_id'>) => busy.get(keyOf(p)) ?? false,
  };
}

export default useCartActions;

